import {
	CoreButton,
	CoreCard,
	CoreChip,
	CoreDatePicker,
	CoreMenuItem,
	CoreModal,
	CoreRadioGroup,
	CoreSelect,
	CoreTextField,
	coreToast,
	RadioOption,
} from "@verity/components/core";
import i18n from "../../i18n";
import { Box, Grid, Link, ListItemText, Skeleton, Typography } from "@mui/material";
import {
	ExportReportInfo,
	ExportReportRadioGroupProps,
	ExportType,
	FormatType,
	ReportType,
	YearSelection,
} from "./report-types";
import { useEffect, useId, useState } from "react";
import styles from "../reports.module.css";
import {
	exportReport,
	fetchDistinctYearsForReport,
	getEmailError,
	getExportModalDropDownLabel,
	getFlagSeverity,
	getReportData,
} from "./helpers";
import ArrowRight from "../../images/reports/arrowRight.svg?react";
import ExportIcon from "../../images/reports/exportIcon.svg?react";
import NoResultImage from "../../images/reports/table-no-results-available.svg?react";
import NoEmailData from "../../images/reports/no-email-data.svg?react";
import AddResultsImage from "../../images/reports/addResults.svg?react";
import SwitchIcon from "../../images/reports/switchIcon.svg?react";

export const badgeStyle = {
	borderRadius: "16px",
	padding: "0px 4px",
	marginLeft: "auto",
	minWidth: "fit-content",
	width: "36px",
	minHeight: "22px",
	fontSize: "14px",
	lineHeight: "1.6",
	fontWeight: "500",
	color: "#575757",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	overflow: "hidden",
	"& .MuiChip-label": {
		whiteSpace: "nowrap",
	},
};

export function FlagLevelBadge({ flagLevel }: { flagLevel: number }) {
	const roundedFlagLevel = Math.round(flagLevel ?? 0);
	const flagLabel = getFlagSeverity(flagLevel);
	const flagColor =
		roundedFlagLevel <= 0
			? "#E6E6E6"
			: roundedFlagLevel <= 1
				? "#DBE7E1"
				: roundedFlagLevel <= 2
					? "#FAEFDE"
					: "#F8DCDB";
	return (
		<CoreChip chipColor={flagColor} sx={{ ...badgeStyle, width: "80px" }}>
			{flagLabel}
		</CoreChip>
	);
}

export const NoResults = ({
	text,
	add,
	emptyEmail,
	description,
}: {
	text: string;
	add?: boolean;
	emptyEmail?: boolean;
	description?: string;
}) => (
	<Box
		display="flex"
		justifyContent="center"
		alignItems="center"
		height="100%"
		width="100%"
		flexDirection="column"
		gap="16px"
	>
		{emptyEmail ? <NoEmailData /> : add ? <AddResultsImage /> : <NoResultImage />}
		{!description && (
			<Typography fontWeight={500} fontSize={"15px"} textAlign="center">
				{text}
			</Typography>
		)}
		{description && (
			<>
				<Typography fontWeight={800} fontSize={"20px"} textAlign="center">
					{text}
				</Typography>
				<Typography fontWeight={500} fontSize={"15px"} textAlign="center">
					{description}
				</Typography>
			</>
		)}
	</Box>
);

function ExportYearDropdown({
	selectedYear,
	onChange,
	availableYears,
}: {
	selectedYear: YearSelection;
	onChange: (value: YearSelection) => void;
	availableYears: number[];
}) {
	const dateDropdownId = useId();
	return (
		<div className={styles.exportRow}>
			<Typography className={styles.exportRowHeader} id={dateDropdownId}>
				{i18n.t("instructor.components.reports.exportModal.headers.filter")}
			</Typography>
			<CoreSelect<YearSelection>
				value={selectedYear}
				renderValue={(value) => getExportModalDropDownLabel(value)}
				onChange={(event) => onChange(event.target.value as YearSelection)}
				labelId={dateDropdownId}
				MenuProps={{
					PaperProps: {
						sx: {
							maxHeight: "200px",
						},
					},
				}}
			>
				<CoreMenuItem value={"current"}>
					<ListItemText>{getExportModalDropDownLabel("current")}</ListItemText>
				</CoreMenuItem>
				{availableYears.map((year) => {
					return (
						<CoreMenuItem key={year} value={year}>
							<ListItemText>{getExportModalDropDownLabel(year)}</ListItemText>
						</CoreMenuItem>
					);
				})}
			</CoreSelect>
		</div>
	);
}

function ExportEmailRow({
	emailError,
	onChange,
}: {
	emailError: string | undefined;
	onChange: (value: string) => void;
}) {
	const emailId = useId();

	return (
		<div className={`${styles.exportRow} ${emailError ? styles.error : ""}`}>
			<Typography id={emailId} className={styles.exportRowHeader}>
				{i18n.t("instructor.components.reports.exportModal.headers.email")}
			</Typography>

			<CoreTextField
				className={styles.emailTextField}
				placeholder={i18n.t("instructor.components.reports.exportModal.emailPlaceHolder")}
				inputProps={{ "aria-labelledby": emailId }}
				onChange={(e) => onChange(e.target.value)}
				error={emailError}
				InputProps={{ sx: { height: "35px" } }}
				required
			/>
		</div>
	);
}

const EXPORT_RADIO_OPTIONS: RadioOption[] = [
	{ value: "download", label: i18n.t("instructor.components.reports.exportModal.exportRadio.download") },
	{ value: "email", label: i18n.t("instructor.components.reports.exportModal.exportRadio.email") },
];

const FORMAT_RADIO_OPTIONS: RadioOption[] = [
	{ value: "json", label: i18n.t("instructor.components.reports.exportModal.formatRadio.json") },
	{ value: "csv", label: i18n.t("instructor.components.reports.exportModal.formatRadio.csv") },
	{ value: "excel", label: i18n.t("instructor.components.reports.exportModal.formatRadio.excel") },
];

function ExportReportRadioGroup({ onChange, selectedValue, type }: ExportReportRadioGroupProps) {
	const radioOptions = type === "export" ? EXPORT_RADIO_OPTIONS : FORMAT_RADIO_OPTIONS;
	const id = useId();
	const header =
		type === "export"
			? i18n.t("instructor.components.reports.exportModal.headers.export")
			: i18n.t("instructor.components.reports.exportModal.headers.format");

	return (
		<div className={styles.exportRow}>
			<Typography className={styles.exportRowHeader} id={id}>
				{header}
			</Typography>
			<CoreRadioGroup
				radioGroupAriaLabelledby={id}
				radioOptions={radioOptions}
				onChange={onChange}
				selectedValue={selectedValue}
				sx={{ marginLeft: "8px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}
			/>
		</div>
	);
}

export function ExportReportButton({ reportInfo }: { reportInfo: ExportReportInfo }) {
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<>
			<CoreButton
				onClick={() => setModalOpen(true)}
				startIcon={<ExportIcon />}
				className={styles.exportButton}
				aria-haspopup="dialog"
			>
				{i18n.t("instructor.components.reports.exportModal.buttons.openModal")}
			</CoreButton>
			{modalOpen && <ExportReportModal reportInfo={reportInfo} onClose={() => setModalOpen(false)} />}
		</>
	);
}

function ExportReportModal({ reportInfo, onClose }: { reportInfo: ExportReportInfo; onClose: () => void }) {
	const reportType = reportInfo.reportType;

	const [exportType, setExportType] = useState<ExportType>("download");
	const [formatType, setFormatType] = useState<FormatType>("excel");
	const [yearsWithData, setYearsWithData] = useState<number[]>([]);
	const [selectedYear, setSelectedYear] = useState<YearSelection>("current");
	const [email, setEmail] = useState<string>("");
	const [emailError, setEmailError] = useState<string | undefined>(getEmailError(email));
	const [pending, setPending] = useState<boolean>(false);

	const today = new Date();
	const filterStartDate =
		selectedYear === "current" ? reportInfo.institutionCourseReportInfo?.startDate : new Date(selectedYear, 0, 1);

	const filterEndDate =
		selectedYear === "current"
			? reportInfo.institutionCourseReportInfo?.endDate
			: selectedYear === today.getFullYear()
				? today
				: new Date(selectedYear, 11, 31);

	const description =
		filterStartDate && filterEndDate
			? i18n.t("instructor.components.reports.exportModal.descriptionWithDate")
			: i18n.t("instructor.components.reports.exportModal.descriptionNoDate");

	// Initialize date dropdown
	useEffect(() => {
		if (reportType === "assessment") return;
		const { id, verityFetch } = reportInfo.institutionCourseReportInfo;
		const loadYears = async () => {
			const years = await fetchDistinctYearsForReport({
				id: String(id),
				verityFetch: verityFetch,
				reportType: reportType,
			});
			if (!years) {
				coreToast.error(i18n.t("instructor.components.reports.exportModal.errors.historicalData"));
				return;
			}
			setYearsWithData(years);
		};
		void loadYears();
	}, [reportInfo.institutionCourseReportInfo, reportType]);

	async function handleExportReport() {
		setPending(true);

		// Get report data
		const data = await getReportData({
			reportType: reportType,
			reportInfo: reportInfo,
			filterStartDate: filterStartDate,
			filterEndDate: filterEndDate,
		});

		// Data will be undefined if there is an error fetching report results
		if (!data || !data["data"]) {
			coreToast.error(i18n.t("instructor.components.reports.exportModal.errors.fetchingData"));
			return;
		}

		const result = await exportReport({
			reportType: reportType,
			startDate: filterStartDate,
			endDate: filterEndDate,
			reportInfo: data["data"],
			name: data["subFileName"],
			formatType: formatType,
			subAccountName: reportInfo.institutionCourseReportInfo?.institutionInfo?.subAccountName,
			exportType: exportType,
			email: email,
		});

		if (result.success) {
			coreToast.success(
				exportType === "download"
					? i18n.t("instructor.components.reports.exportModal.downloadSuccess")
					: i18n.t("instructor.components.reports.exportModal.emailSuccess"),
				{ autoClose: 1500 },
			);
		} else {
			coreToast.error(result.error);
		}

		onClose();
	}

	return (
		<CoreModal
			title={i18n.t("instructor.components.reports.exportModal.title")}
			open
			onClose={onClose}
			buttons={[
				{
					text: i18n.t("instructor.components.reports.exportModal.buttons.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("instructor.components.reports.exportModal.buttons.export"),
					color: "primary",
					onClick: handleExportReport,
					disabled: (exportType === "email" && emailError !== undefined) || pending,
				},
			]}
		>
			<>
				<Typography className={styles.exportReportDescription}>{description}</Typography>
				<ExportReportRadioGroup
					type="export"
					onChange={(_, value) => setExportType(value as ExportType)}
					selectedValue={exportType}
				/>
				{exportType === "email" && (
					<ExportEmailRow
						onChange={(value) => {
							setEmail(value);
							setEmailError(getEmailError(value));
						}}
						emailError={emailError}
					/>
				)}
				<ExportReportRadioGroup
					type="format"
					onChange={(_, value) => setFormatType(value as FormatType)}
					selectedValue={formatType}
				/>
				{filterStartDate && filterEndDate && (
					<ExportYearDropdown
						selectedYear={selectedYear}
						availableYears={yearsWithData}
						onChange={(value) => setSelectedYear(value)}
					/>
				)}
			</>
		</CoreModal>
	);
}

export function ReportHeader({
	setRefreshReportModalOpen,
	lastUpdatedDate,
	startDate,
	endDate,
	setStartDate,
	setEndDate,
	type,
	showRefreshButton,
	body,
	fixedDate,
	switchButton,
	reportInfo,
}: {
	setRefreshReportModalOpen: (open: boolean) => void;
	lastUpdatedDate?: Date;
	startDate: Date;
	endDate: Date;
	setStartDate: (date: Date) => void;
	setEndDate: (date: Date) => void;
	type: ReportType;
	showRefreshButton?: boolean;
	body?: React.ReactNode;
	fixedDate?: boolean;
	switchButton?: {
		text: string;
		onClick: () => void;
	};
	reportInfo: ExportReportInfo;
}) {
	const date = new Date();
	const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
	lastDayOfMonth.setHours(23, 59, 59, 999);
	useEffect(() => {
		if (startDate > endDate) {
			setEndDate(lastDayOfMonth);
		}
	}, [startDate]);

	const options: Intl.DateTimeFormatOptions = {
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: true,
	};

	return (
		<CoreCard>
			<Grid container spacing={"10px"}>
				<Grid item container xs={12} md={true} display="flex" flexDirection="column" gap="10px">
					<Typography
						variant="h1"
						sx={{
							fontWeight: 700,
							fontSize: "20px",
							color: "black",
							textAlign: "left",
							lineHeight: "1.4",
						}}
					>
						{i18n.t(`instructor.components.reports.typeTitle.${type}`)}
					</Typography>
					<Box display="flex" flexDirection="column" gap="2px">
						{body}
						<Typography fontWeight={400} fontSize={"15px"} color={"#6b6b6b"}>
							{i18n.t(`instructor.components.reports.typeDesc.${type}`)}
						</Typography>
					</Box>
				</Grid>
				<Grid
					item
					xs={12}
					md="auto"
					container
					direction="column"
					gap={{ xs: "10px", sm: "10px", md: "10px", lg: "16px" }}
					minHeight={"100%"}
				>
					{lastUpdatedDate ? (
						<Grid item container alignItems="flex-end" gap="6px" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
							<Typography fontWeight={400} fontSize={"15px"} color="black">
								{i18n.t("instructor.components.reports.updated")}
							</Typography>
							<Typography fontWeight={400} fontSize={"15px"} color={"#6B6B6B"}>
								{lastUpdatedDate.getTime() === new Date(0).getTime()
									? i18n.t("instructor.components.reports.never")
									: lastUpdatedDate.toLocaleDateString(i18n.language, options)}
							</Typography>
							{showRefreshButton && (
								<Link
									aria-haspopup="dialog"
									component="button"
									underline="hover"
									onClick={() => setRefreshReportModalOpen(true)}
								>
									{i18n.t("instructor.components.reports.refresh")}
								</Link>
							)}
						</Grid>
					) : (
						<Grid item container alignItems="center" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
							<Skeleton variant="text" width={320} height={22.5} />
						</Grid>
					)}
					<Grid item container alignItems="stretch" gap="10px" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
						{switchButton && (
							<CoreButton
								aria-haspopup="dialog"
								sx={{ width: "fit-content", maxWidth: "100%", display: "flex", gap: "6px" }}
								onClick={switchButton?.onClick}
							>
								<SwitchIcon />
								{switchButton?.text}
							</CoreButton>
						)}
						<ExportReportButton reportInfo={reportInfo} />
					</Grid>
					<Grid item container alignItems="center" gap="10px" justifyContent={{ md: "flex-start", lg: "flex-end" }}>
						{fixedDate ? (
							<Grid item container alignItems="center" gap="6px" justifyContent={{ md: "flex-start", lg: "flex-end" }}>
								<Typography fontWeight={400} fontSize={"15px"}>
									{i18n.t("instructor.components.reports.date")}
								</Typography>
								<Typography fontWeight={400} fontSize={"15px"} color={"#6B6B6B"}>
									{startDate.toLocaleString("default", {
										weekday: "long",
										year: "numeric",
										month: "long",
										day: "numeric",
									})}
								</Typography>
							</Grid>
						) : (
							<Grid item container gap="6px" alignItems={"center"}>
								{/* {switchButton && switchButton.length > 0 && (
                                    <CoreSelect
                                        value={""}
                                        label={"Filter Report"}
                                        renderValue={() => "Filter Report"}
                                        sx={{ width: "210px" }}
                                    >
                                        {switchButton.map((option) => (
                                            <MenuItem key={""} onClick={option.onClick}>
                                                {option.text}
                                            </MenuItem>
                                        ))}
                                    </CoreSelect>
                                )} */}
								<Typography fontWeight={400} fontSize={"15px"}>
									{i18n.t("instructor.components.reports.filterByDate")}
								</Typography>
								<Box display="flex" gap="6px" flexWrap="wrap" alignItems="center" justifyContent="flex-start">
									<CoreDatePicker
										format="MMMM yyyy"
										disableFuture
										defaultValue={startDate}
										views={["year", "month"]}
										onMonthChange={(date) => {
											const firstDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
											setStartDate(firstDateOfMonth);
										}}
										dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartDialog")}
										ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarStartInput")}
										useButton
										slotProps={{
											textField: {
												InputProps: {
													sx: {
														height: "36px",
														maxWidth: "185px",
														"&&& input": { fontSize: "15px" },
													},
												},
											},
											popper: { placement: "bottom-start" },
										}}
									/>
									<ArrowRight />
									<CoreDatePicker
										format="MMMM yyyy"
										disableFuture
										views={["year", "month"]}
										defaultValue={new Date()}
										onMonthChange={(date) => {
											const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
											lastDayOfMonth.setHours(23, 59, 59, 999);
											setEndDate(lastDayOfMonth);
										}}
										dialogAriaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndDialog")}
										ariaLabel={i18n.t("instructor.components.reports.ariaLabels.calendarEndInput")}
										minDate={startDate}
										useButton
										slotProps={{
											textField: {
												InputProps: {
													sx: {
														height: "36px",
														maxWidth: "185px",
														"&&& input": { fontSize: "15px" },
													},
												},
											},
											popper: { placement: "bottom-start" },
										}}
									/>
								</Box>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Grid>
		</CoreCard>
	);
}
